import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import {
  XMarkIcon,
  BuildingLibraryIcon,
  UsersIcon,
  ClockIcon,
  TagIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

import ViewAssignedVotersTab from "./ViewAssignedVotersTab";
import { votersAPI } from "../../../services/api";
import { useQuery } from "react-query";

function ViewPrecinctAssignmentModal({ isOpen, onClose, precinct }) {
  const [activeTab, setActiveTab] = useState("details");

  const precinctId = precinct?.precinct_id;
  const votersQuery = useQuery(
    ["voters-by-precinct", precinctId],
    async () => {
      const res = await votersAPI.getVotersByPrecinctId(precinctId);
      return (
        res?.data?.data?.voters_by_precinct ||
        res?.data?.data?.voters ||
        res?.data?.data ||
        []
      );
    },
    {
      enabled: isOpen && activeTab === "voters" && !!precinctId,
      staleTime: 30_000,
    }
  );

  if (!precinct) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-6 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-blue-200 text-blue-600 text-sm rounded-md">
                      {precinct.name}
                    </span>
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-gray-900"
                    >
                      Precinct Assignment
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200"
                  >
                    <XMarkIcon className="w-6 h-6 text-gray-500" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex items-center border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab("details")}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 font-medium ${
                      activeTab === "details"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <BuildingLibraryIcon className="w-5 h-5" />
                    Precinct Details
                  </button>
                  <button
                    onClick={() => setActiveTab("voters")}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 font-medium ${
                      activeTab === "voters"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <UsersIcon className="w-5 h-5" />
                    Assigned Voters
                  </button>
                </div>

                {/* Content */}
                {activeTab === "details" && (
                  <div className="p-6">
                    <div className="border border-gray-200 rounded-2xl p-6 space-y-6">
                      {/* Last Updated */}
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <ClockIcon className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-gray-500 text-sm">Last Updated on</p>
                          <p className="font-semibold">September 15, 2025</p>
                        </div>
                      </div>

                      <hr className="border-gray-200" />

                      {/* Grid Details */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Precinct Name */}
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <TagIcon className="w-3 h-3 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm">Precinct Name</p>
                            <p className="font-medium">{precinct.name}</p>
                          </div>
                        </div>

                        {/* Total Assigned Voters */}
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <UsersIcon className="w-3 h-3 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm">
                              Total Assigned Voters
                            </p>
                            <p className="font-medium">{precinct.total_assigned_voters}</p>
                          </div>
                        </div>

                        {/* Location */}
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <MapPinIcon className="w-3 h-3 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm">Location</p>
                            <p className="font-medium">{precinct.location}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "voters" && (
                  <ViewAssignedVotersTab voters={Array.isArray(votersQuery.data) ? votersQuery.data : []} />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default ViewPrecinctAssignmentModal;
